import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  Bitcoin,
  Check,
  Check as CheckIcon,
  CheckCircle2,
  Copy,
  CreditCard,
  Download,
  Loader2,
} from 'lucide-react';
import { fetchDonsModes, fetchEtatDon, soumettreDon } from '../services/api';
import { nomFamille, prenomUsage } from '../utils/nom';

const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api';

const schema = yup.object({
  prenom: yup.string().nullable(),
  nom: yup.string().nullable(),
  anonyme: yup.boolean(),
  montant: yup
    .number()
    .typeError('Saisissez un montant valide')
    .positive('Le montant doit être positif')
    .min(1, 'Le montant minimum est de 1 €')
    .required('Le montant est requis'),
  modePaiement: yup.string().required('Choisissez un mode de paiement'),
});

type FormData = yup.InferType<typeof schema>;

const modeIcons: Record<string, any> = {
  carte: CreditCard,
  crypto: Bitcoin,
};

export default function DonLibre() {
  const [params, setParams] = useSearchParams();
  // Trois postures possibles : donner son nom et demander un reçu, donner son
  // nom sans reçu, ou rester anonyme. Le reçu ferme l'anonymat — il porte une
  // adresse, sans quoi il n'atteste de rien d'utile.
  const [anonyme, setAnonyme] = useState(false);
  const [souhaiteRecu, setSouhaiteRecu] = useState(false);
  const [copie, setCopie] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [coordonnees, setCoordonnees] = useState({
    email: '',
    adresse: '',
    codePostal: '',
    ville: '',
  });

  const { data: modesData } = useQuery({
    queryKey: ['dons-modes'],
    queryFn: fetchDonsModes,
  });

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      prenom: '',
      nom: '',
      anonyme: false,
      montant: undefined,
      modePaiement: '',
    },
  });

  const mutation = useMutation({
    mutationFn: soumettreDon,
    onSuccess: (d: any) => {
      if (d.url) {
        window.location.href = d.url;
      }
    },
    onError: (e: Error) => setErreur(e.message),
  });

  const modePaiement = watch('modePaiement');
  const montant = watch('montant');
  const modes = modesData?.modes ?? [];

  // Le montant saisi, converti en BTC au taux du jour (CoinGecko), sert a
  // pre-remplir le QR code bitcoin. En cas d'echec de la requete, le QR code
  // est quand meme genere sans montant.
  const { data: prixBtc } = useQuery({
    queryKey: ['btc-eur'],
    queryFn: async () => {
      const res = await fetch(
        'https://api.coingecko.com/api/v3/simple/price?ids=bitcoin&vs_currencies=eur'
      );
      const json = await res.json();
      return json?.bitcoin?.eur ?? null;
    },
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  const montantBtc =
    montant && montant > 0 && prixBtc ? Number((montant / prixBtc).toFixed(8)) : null;

  const retour = params.get('don');
  const reference = params.get('reference');

  const onSubmit = (data: FormData) => {
    setErreur(null);
    mutation.mutate({
      prenom: anonyme ? undefined : data.prenom || undefined,
      nom: anonyme ? undefined : data.nom || undefined,
      anonyme,
      souhaiteRecu,
      email: souhaiteRecu ? coordonnees.email : undefined,
      adresse: souhaiteRecu ? coordonnees.adresse : undefined,
      codePostal: souhaiteRecu ? coordonnees.codePostal : undefined,
      ville: souhaiteRecu ? coordonnees.ville : undefined,
      montant: data.montant,
      modePaiement: data.modePaiement,
    });
  };

  if (retour === 'succes' && reference) {
    return <RetourDon reference={reference} onRecommencer={() => setParams(new URLSearchParams())} />;
  }

  return (
    <div className="p-4 lg:p-0 space-y-5 lg:space-y-6 pb-24 lg:pb-0">
      <div>
        <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-gray-100">Don libre</h1>
        <p className="mt-2 text-sm lg:text-base text-gray-600 max-w-3xl dark:text-gray-400">
          Soutenez la fête locale par un don libre.
        </p>
      </div>

      {retour === 'annule' && (
        <p className="text-sm rounded-xl px-3 py-2 bg-gray-100 text-gray-700 dark:text-gray-300 dark:bg-gray-800">
          Paiement abandonné. Rien n'a été prélevé.
        </p>
      )}

      {mutation.isSuccess && !mutation.data?.url && (
        <div className="card bg-green-50 border-green-200 text-green-800 lg:p-6">
          <div className="flex items-center gap-2 font-semibold">
            <Check className="w-5 h-5" />
            Merci pour votre don !
          </div>
          <p className="mt-2 text-sm lg:text-base">{mutation.data?.message}</p>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5 lg:space-y-6">
        <section className="card space-y-4 lg:p-6">
          <h2 className="font-semibold lg:text-lg">Vos coordonnées</h2>

          <label className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              checked={souhaiteRecu}
              onChange={(e) => {
                setSouhaiteRecu(e.target.checked);
                if (e.target.checked) setAnonyme(false);
              }}
              className="mt-0.5"
            />
            <span>
              <span className="font-medium">Je souhaite un reçu nominatif</span>
              <span className="block text-xs text-gray-500 dark:text-gray-400">
                Il porte votre nom et votre adresse, et vous parvient par e-mail. Le demander
                suppose donc de vous identifier.
              </span>
            </span>
          </label>

          {!souhaiteRecu && (
            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
              <input
                type="checkbox"
                checked={anonyme}
                onChange={(e) => setAnonyme(e.target.checked)}
                className="mt-0.5"
              />
              Rester anonyme
            </label>
          )}

          {!anonyme && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* La mise en forme se voit à la frappe : le donateur découvre
                  la règle du comité ici, pas sur son justificatif. */}
              <input
                className="input"
                placeholder={souhaiteRecu ? 'Prénom *' : 'Prénom (optionnel)'}
                {...register('prenom', {
                  onChange: (e) => setValue('prenom', prenomUsage(e.target.value)),
                })}
              />
              <input
                className="input"
                placeholder={souhaiteRecu ? 'Nom *' : 'Nom (optionnel)'}
                {...register('nom', {
                  onChange: (e) => setValue('nom', nomFamille(e.target.value)),
                })}
              />
            </div>
          )}

          {souhaiteRecu && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <input
                className="input lg:col-span-2"
                type="email"
                placeholder="E-mail *"
                value={coordonnees.email}
                onChange={(e) => setCoordonnees({ ...coordonnees, email: e.target.value })}
              />
              <input
                className="input lg:col-span-2"
                placeholder="Adresse *"
                value={coordonnees.adresse}
                onChange={(e) => setCoordonnees({ ...coordonnees, adresse: e.target.value })}
              />
              <input
                className="input"
                placeholder="Code postal *"
                value={coordonnees.codePostal}
                onChange={(e) => setCoordonnees({ ...coordonnees, codePostal: e.target.value })}
              />
              <input
                className="input"
                placeholder="Ville *"
                value={coordonnees.ville}
                onChange={(e) => setCoordonnees({ ...coordonnees, ville: e.target.value })}
              />
            </div>
          )}

          {anonyme && (
            <p className="text-sm rounded-xl px-3 py-2 bg-amber-50 text-amber-900 dark:bg-amber-900/25 dark:text-amber-100 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>
                Vous resterez anonyme : nous ne conserverons ni votre nom, ni votre adresse.
                Votre justificatif de paiement vous sera proposé au téléchargement juste après
                le règlement, et ne pourra plus être retrouvé ensuite.
              </span>
            </p>
          )}
        </section>

        <section className="card space-y-4 lg:p-6">
          <h2 className="font-semibold lg:text-lg">Montant</h2>
          <div className="flex items-center gap-2">
            <input
              type="number"
              min="1"
              step="1"
              className="input"
              placeholder="Montant en euros"
              {...register('montant')}
            />
            <span className="text-xl font-bold text-primary-700">€</span>
          </div>
          {errors.montant && <p className="text-xs text-red-600">{errors.montant.message}</p>}
        </section>

        <section className="card space-y-4 lg:p-6">
          <h2 className="font-semibold lg:text-lg">Mode de paiement en ligne</h2>
          {(!montant || montant <= 0) && (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Saisissez d'abord un montant supérieur à zéro pour choisir un mode de paiement.
            </p>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {modes.map((mode: any) => {
              const Icon = modeIcons[mode.id] ?? CreditCard;
              const selected = modePaiement === mode.id;
              const verrouille = !montant || montant <= 0;
              return (
                <button
                  key={mode.id}
                  type="button"
                  onClick={() => !verrouille && setValue('modePaiement', mode.id)}
                  disabled={verrouille}
                  className={`card text-left p-4 transition border-2 ${
                    selected
                      ? 'border-primary-600 bg-primary-50'
                      : 'border-transparent hover:border-primary-300'
                  } ${verrouille ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <div className="flex items-start justify-between">
                    <Icon className="w-6 h-6 text-primary-700" />
                    {mode.actif ? (
                      <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded">
                        Disponible
                      </span>
                    ) : (
                      <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded">
                        Bientôt disponible
                      </span>
                    )}
                  </div>
                  <p className="mt-3 font-semibold text-gray-900 dark:text-gray-100">{mode.label}</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{mode.description}</p>
                </button>
              );
            })}
          </div>

          {modePaiement !== 'carte' && modePaiement !== '' && (
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Seul le paiement par carte est encaissé en ligne : lui seul donne lieu à un
              justificatif immédiat. Pour les autres moyens, le bureau vous recontactera.
            </p>
          )}

          {modePaiement === 'crypto' &&
            (() => {
              const modeCrypto = modes.find((m: any) => m.id === 'crypto');
              const adresse = modeCrypto?.adresse;
              if (!adresse) return null;
              // Le QR ne porte que l'adresse. Y inscrire un montant converti
              // au taux du jour engageait le donateur sur un cours périmé dès
              // le scan, et bloquait un portefeuille qui pré-remplit lui-même.
              const uri = `bitcoin:${adresse}`;
              return (
                <div className="card bg-gray-50 p-4 space-y-3">
                  <p className="font-semibold text-gray-900 dark:text-gray-100">Donner en Bitcoin</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Envoyez votre don à l'adresse suivante.
                    {montantBtc && (
                      <>
                        {' '}
                        À titre indicatif, <strong>{montant} €</strong> représentent environ{' '}
                        <strong>{montantBtc} BTC</strong> au taux du jour.
                      </>
                    )}
                  </p>
                  <div className="flex items-center gap-2 flex-wrap">
                    <code className="px-3 py-2 bg-white border rounded text-xs font-mono break-all flex-1 min-w-[200px]">
                      {adresse}
                    </code>
                    <button
                      type="button"
                      onClick={() => {
                        navigator.clipboard?.writeText(adresse);
                        setCopie(true);
                        setTimeout(() => setCopie(false), 2000);
                      }}
                      className="btn-primary px-3 py-2 text-sm inline-flex items-center gap-1"
                    >
                      {copie ? <CheckIcon className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      {copie ? 'Copié' : 'Copier'}
                    </button>
                  </div>
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                    <img
                      src={`https://api.qrserver.com/v1/create-qr-code/?size=160x160&data=${encodeURIComponent(uri)}`}
                      alt="QR code Bitcoin"
                      width={120}
                      height={120}
                      className="rounded bg-white p-1 border"
                    />
                    <p className="text-xs text-gray-500 max-w-xs dark:text-gray-400">
                      Scannez ce QR code avec votre portefeuille Bitcoin : il contient l'adresse de
                      l'association. Vous saisissez le montant dans votre portefeuille.
                    </p>
                  </div>
                </div>
              );
            })()}
          {errors.modePaiement && (
            <p className="text-xs text-red-600">{errors.modePaiement.message}</p>
          )}
        </section>

        {erreur && (
          <p className="text-sm rounded-xl px-3 py-2 bg-red-50 text-red-700">{erreur}</p>
        )}

        <button
          type="submit"
          disabled={mutation.isPending}
          className="btn-primary w-full lg:w-auto lg:px-8 disabled:opacity-60 inline-flex items-center justify-center gap-2"
        >
          {mutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
          {mutation.isPending ? 'Envoi en cours…' : 'Faire un don'}
        </button>
      </form>
    </div>
  );
}

/**
 * Retour de paiement d'un don.
 *
 * Pour un donateur anonyme, c'est le seul et unique moment où son justificatif
 * existe : il n'a laissé ni nom ni adresse, rien ne permettra de le lui
 * renvoyer. Le message le dit sans détour, et le bouton est le point d'orgue
 * de l'écran.
 */
function RetourDon({
  reference,
  onRecommencer,
}: {
  reference: string;
  onRecommencer: () => void;
}) {
  const [telecharge, setTelecharge] = useState(false);

  const { data, refetch } = useQuery({
    queryKey: ['etat-don', reference],
    queryFn: () => fetchEtatDon(reference),
    retry: false,
    // Le webhook Stripe confirme le paiement à son rythme : on relit tant que
    // le don n'est pas encore marqué payé.
    refetchInterval: (q) => (q.state.data?.don?.statut === 'paye' ? false : 2000),
  });

  const don = data?.don;
  const jeton = don?.justificatifJeton ?? null;

  useEffect(() => {
    if (don?.statut === 'paye') refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [don?.statut]);

  return (
    <div className="p-4 lg:p-0 space-y-5 pb-24 lg:pb-0 max-w-2xl">
      <div className="rounded-2xl border border-green-200 bg-green-50 dark:bg-green-900/20 dark:border-green-800 p-5 space-y-3">
        <div className="flex items-start gap-3">
          <CheckCircle2 className="w-7 h-7 text-green-600 dark:text-green-400 shrink-0 mt-0.5" />
          <div>
            <p className="text-xl lg:text-2xl font-semibold text-green-900 dark:text-green-100">
              Merci pour votre don
            </p>
            <p className="text-sm text-green-800/80 dark:text-green-200/80 mt-0.5">
              Il fera vivre les fêtes du village.
            </p>
          </div>
        </div>

        <div className="rounded-xl bg-white dark:bg-gray-900 border border-green-200 dark:border-green-800 px-4 py-3">
          <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Référence de votre don
          </p>
          <p className="font-mono text-xl font-bold text-green-700 dark:text-green-400 tracking-wider">
            {reference}
          </p>
          {don?.montant ? (
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{don.montant} €</p>
          ) : null}
        </div>
      </div>

      {don?.statut !== 'paye' && (
        <p className="card flex items-center gap-3 text-sm text-gray-600 dark:text-gray-400">
          <Loader2 className="w-4 h-4 animate-spin text-primary-600" />
          Nous attendons la confirmation de votre banque. Restez sur cette page quelques
          instants.
        </p>
      )}

      {don?.statut === 'paye' && don?.souhaiteRecu && (
        <p className="card text-sm text-gray-700 dark:text-gray-300">
          Votre reçu nominatif vient de partir par e-mail, avec le justificatif de paiement en
          pièce jointe. Pensez à regarder dans vos indésirables s'il tarde à arriver.
        </p>
      )}

      {don?.statut === 'paye' && !don?.souhaiteRecu && jeton && (
        <section className="rounded-2xl border-2 border-amber-300 bg-amber-50 dark:bg-amber-950/30 p-5 space-y-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-6 h-6 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
            <div>
              <p className="font-bold text-amber-900 dark:text-amber-100">
                Votre justificatif est disponible maintenant
              </p>
              <p className="text-sm text-amber-800 dark:text-amber-200 mt-1 leading-relaxed">
                Vous avez choisi de rester anonyme : nous ne conservons ni votre nom, ni votre
                adresse, et ne pourrons donc pas vous le renvoyer. Ce document n'est pas archivé
                sur nos serveurs — il se fabrique à l'instant où vous cliquez, une seule fois.
                <strong> Téléchargez-le avant de quitter cette page&nbsp;: ensuite, il sera
                définitivement hors de portée.</strong>
              </p>
            </div>
          </div>

          <a
            href={`${API_BASE}/dons/public/${encodeURIComponent(reference)}/justificatif.pdf?jeton=${encodeURIComponent(jeton)}`}
            onClick={() => setTelecharge(true)}
            className="btn-primary w-full justify-center py-3 inline-flex items-center gap-2"
          >
            <Download className="w-5 h-5" />
            Télécharger mon justificatif de paiement
          </a>

          {telecharge && (
            <p className="text-xs text-amber-800 dark:text-amber-300 text-center">
              Vérifiez que le fichier est bien arrivé dans vos téléchargements avant de fermer
              cette page.
            </p>
          )}
        </section>
      )}

      {don?.statut === 'paye' && !don?.souhaiteRecu && !jeton && (
        <p className="card text-sm text-gray-600 dark:text-gray-400">
          Le justificatif de ce don a déjà été téléchargé. Comme annoncé, il n'est plus
          accessible : votre don étant anonyme, nous n'en conservons aucune copie.
        </p>
      )}

      <button onClick={onRecommencer} className="btn-outline w-full lg:w-auto">
        Faire un autre don
      </button>
    </div>
  );
}
